import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';

import { InvitationService } from 'src/app/services/invitation/invitation.service';
import { ToastrService } from 'ngx-toastr';
import { FormControlDefinition } from '../../model/FormControlDefinition';
import { noWhitespaceValidator } from '../../shared/no-whitespace.validator';
import { DateTime } from 'luxon';
import { AppConstants } from 'src/app/constants/app-constants';
import { Invitation } from 'src/app/model/Invitation';
import { HttpErrorResponse } from '@angular/common/http';
import { finalize } from 'rxjs';
import { DialogService } from 'src/app/services/dialog/dialog.service';

@Component({
  selector: 'new-app-dialog',
  templateUrl: './new-app-dialog.component.html',
  styleUrls: ['./new-app-dialog.component.scss'], // Note: Changed "styleUrl" to "styleUrls"
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class NewAppDialogComponent {
  private readonly _invitationService = inject(InvitationService);
  private readonly _toastrService: ToastrService = inject(ToastrService);
  private readonly _dialogRef: MatDialogRef<NewAppDialogComponent> =
    inject(MatDialogRef);
  private readonly _dialogService = inject(DialogService);
  public isSubmitting: boolean = false;

  // Define the form group using the key "business_name"
  public submitInvGroup: FormGroup = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
    name: new FormControl('', [Validators.required, noWhitespaceValidator()]),
    lastName: new FormControl('', [
      Validators.required,
      noWhitespaceValidator(),
    ]),
    business_name: new FormControl('', [
      Validators.required,
      noWhitespaceValidator(),
    ]),
  });

  /** Define the form controls for rendering in the template */
  public submitAppDefinition: FormControlDefinition[] = [
    {
      title: 'First Name',
      name: 'name',
      type: 'text', // Using text type for a name input
      placeholder: 'First Name',
      control: this.submitInvGroup.controls['name'],
      editable: true,
    },
    {
      title: 'Last Name',
      name: 'lastName',
      type: 'text', // Using text type for a name input
      placeholder: 'Last Name',
      control: this.submitInvGroup.controls['lastName'],
      editable: true,
    },
    {
      title: 'Recipient Email',
      name: 'email',
      type: 'email',
      placeholder: 'Recipient Email',
      control: this.submitInvGroup.controls['email'],
      editable: true,
    },
    {
      title: 'Business Name',
      name: 'business_name', // Make sure this matches the form group key
      type: 'text', // Using text type for a business name input
      placeholder: 'Business Name',
      control: this.submitInvGroup.controls['business_name'],
      editable: true,
    },
  ];

  /**
   * Submit the new application form
   */
  submit() {
    // Create a new Invitation object from the form values
    const invitation: Invitation = new Invitation(
      this.submitInvGroup.value.email,
      `${this.submitInvGroup.value.name} ${this.submitInvGroup.value.lastName}`,
      this.submitInvGroup.value.business_name,
      DateTime.now().toFormat(AppConstants.DATE_FORMAT),
    );

    // Send the invitation using the InvitationService
    this.isSubmitting = true;
    try {
      this._invitationService
        .sendInvitation(invitation)
        .pipe(finalize(() => (this.isSubmitting = false)))
        .subscribe({
          next: (invitation: Invitation) => {
            this._toastrService.success(
              'Invitation sent successfully to ' + invitation.email,
            );
            this._dialogRef.close();
          },
          error: (error: HttpErrorResponse) => {
            // Show toast to the user
            console.error('[NEW-APP-DIALOG]: Error sending invitation:', error);
            const msg = error.error?.message || 'Unknown error';
            this._toastrService.error('Invite failed: ' + msg);
          },
        });
    } catch (error: any) {
      // Catch errors thrown during the send process
      console.error('[NEW-APP-DIALOG]: Error sending invitation:', error);

      this._toastrService.error('Failed to send invitation');
      this.isSubmitting = false;
    }
  }

  close() {
    this._dialogRef.close();
  }

  /** Opens Settings with the Important Links section (public application links). */
  openSettingsImportantLinks(): void {
    this._dialogService
      .openSettingsModal({ initialMenu: 'Important Links' })
      .subscribe();
  }
}
